import Markdown from "react-markdown";
import type {
  AgentResponsePayload,
  AgentTableJSON,
  ChatMessage,
} from "../api/types";
import { useLocale } from "../hooks/useLocale";
import { formatStepLabel } from "../utils/stepLabel";
import { MultiTableView } from "./MultiTableView";
import styles from "./MessageBubble.module.css";

const MARKDOWN_ALLOWED = ["p", "strong", "em", "ul", "ol", "li", "br"];

interface Props {
  message: ChatMessage;
  // When provided (general chats), renders a "Save as report" button under an
  // assistant message that carries SQL — creates a new report from this выгрузка.
  onSaveAsReport?: (messageId: number) => void;
  // When provided (editor chats), renders a "Save to report" button under an
  // assistant message that carries SQL — overwrites the edited report.
  onSaveToReport?: (messageId: number) => void;
  // Disables the save action while a save/stream is in flight.
  saveDisabled?: boolean;
}

function pickPayload(msg: ChatMessage): AgentResponsePayload | null {
  if (msg.payload && typeof msg.payload === "object") return msg.payload;
  return null;
}

function pickTables(payload: AgentResponsePayload | null): AgentTableJSON[] {
  if (payload?.tables && payload.tables.length > 0) {
    return payload.tables;
  }
  if (payload?.result_columns && payload.result_rows) {
    return [
      {
        columns: payload.result_columns,
        rows: payload.result_rows,
      },
    ];
  }
  return [];
}

export function MessageBubble({
  message,
  onSaveAsReport,
  onSaveToReport,
  saveDisabled,
}: Props) {
  const { t } = useLocale();
  const payload = pickPayload(message);
  const tables = pickTables(payload);
  const pivot = payload?.pivot ?? null;
  const text = message.content?.trim() || payload?.text?.trim() || "";

  if (message.role === "user") {
    return (
      <div className={`${styles.bubble} ${styles.user}`}>
        <span className={styles.author}>{t("chat.user")}</span>
        <p className={styles.text}>{text}</p>
      </div>
    );
  }

  if (message.role === "error") {
    return (
      <div className={`${styles.bubble} ${styles.error}`}>
        <span className={styles.author}>{t("chat.assistant")}</span>
        <p className={styles.text}>{text || t("errors.unknown")}</p>
      </div>
    );
  }

  const stopReason = payload?.stop_reason;
  let stopHint: string | null = null;
  if (stopReason === "limit_hit") stopHint = t("stop_reason.limit_hit");
  else if (stopReason === "error") stopHint = t("stop_reason.error");
  else if (stopReason === "cancelled") stopHint = t("stop_reason.cancelled");

  // A saveable выгрузка is a persisted assistant message that produced SQL.
  // Optimistic placeholders carry a negative id and no SQL, so they're excluded.
  const canSave = message.id > 0 && Boolean(message.generated_sql);

  return (
    <div className={`${styles.bubble} ${styles.assistant}`}>
      <span className={styles.author}>{t("chat.assistant")}</span>
      {text && (
        <div className={styles.markdown}>
          <Markdown allowedElements={MARKDOWN_ALLOWED} unwrapDisallowed>
            {text}
          </Markdown>
        </div>
      )}
      {stopHint && <p className={styles.hint}>{stopHint}</p>}
      {tables.length > 0 && <MultiTableView tables={tables} pivot={pivot} />}
      {canSave && (onSaveAsReport || onSaveToReport) && (
        <div className={styles.saveRow}>
          {onSaveAsReport && (
            <button
              type="button"
              className={styles.saveAction}
              onClick={() => onSaveAsReport(message.id)}
              disabled={saveDisabled}
            >
              {t("msg.save_as_report")}
            </button>
          )}
          {onSaveToReport && (
            <button
              type="button"
              className={styles.saveAction}
              onClick={() => onSaveToReport(message.id)}
              disabled={saveDisabled}
            >
              {t("report.save_to_report")}
            </button>
          )}
        </div>
      )}
      {payload?.steps && payload.steps.length > 0 && (
        <details className={styles.steps}>
          <summary>{payload.steps.length} steps</summary>
          <ol>
            {payload.steps.map((s, i) => (
              <li key={i} className={s.is_error ? styles.stepError : ""}>
                {formatStepLabel(s.tool_name, s.tool_input, t)} · {s.duration_ms} ms
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
